import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  largeTextSize,
} from '../../../styling/stylingHelper';
import { InventoryCount } from '../../../services/inventory/inventoryService';
import { useInventoryStore } from '../../../stores/appInventoryStore';

interface Props {
  /** The session's counts — null closes the modal. All of them move to the new date. */
  counts: InventoryCount[] | null;
  restaurantId: string;
  onClose: () => void;
}

/** `yyyy-MM-dd` in the browser's timezone (what `<input type="date">` expects). */
function toDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Move a whole session to the day it was physically counted. Staff sometimes finalize a
 * session days late, and `counted_at` is stamped at finalize — every downstream read
 * (current value, session grouping, previous-session comparison, consumption) uses that date,
 * so it has to be correctable. Only the DATE moves: each count keeps its own time-of-day, so
 * the sections stay in the order they were saved in.
 */
export default function EditCountDateModal({ counts, restaurantId, onClose }: Props) {
  const { t } = useTranslation();
  const updateSessionDate = useInventoryStore((s) => s.updateSessionDate);

  const [dateValue, setDateValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isOpen = counts != null && counts.length > 0;
  // The session's date = its latest count (what the History row shows).
  const sessionDate = isOpen
    ? counts.reduce((latest, c) => (c.countedAt > latest ? c.countedAt : latest), counts[0].countedAt)
    : null;

  useEffect(() => {
    if (!isOpen || !sessionDate) return;
    setDateValue(toDateInputValue(sessionDate));
    setSaving(false);
    setHasError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, counts?.[0]?.id]);

  if (!isOpen || !sessionDate) return null;

  const handleSave = async () => {
    if (!dateValue || saving) return;
    const [year, month, day] = dateValue.split('-').map(Number);
    if (!year || !month || !day) return;

    setSaving(true);
    setHasError(false);
    try {
      // Keep each count's own time-of-day, only move the calendar day (local time — the
      // timezone the date is displayed in).
      const items = counts.map((c) => ({
        id: c.id,
        countedAt: new Date(
          year,
          month - 1,
          day,
          c.countedAt.getHours(),
          c.countedAt.getMinutes(),
          c.countedAt.getSeconds()
        ),
      }));
      await updateSessionDate(restaurantId, items);
      onClose();
    } catch (err) {
      console.error('Failed to update inventory count date:', err);
      setHasError(true);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={cn('bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-md border', borderPrimary)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          <div className="flex justify-between items-start mb-4">
            <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>{t('inventory.editDateTitle')}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={t('inventory.cancel') as string}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className={cn('mb-4', secondaryText, smallTextSize)}>
            {t('inventory.editDateDescription', { count: counts.length })}
          </p>

          {hasError && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{t('inventory.editDateError')}</p>
            </div>
          )}

          <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
            {t('inventory.editDateLabel')}
          </label>
          <input
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            className={cn(
              'w-full h-11 px-3 rounded-lg border outline-none bg-white dark:bg-[#14344C]',
              borderPrimary,
              primaryText,
              'text-[16px] leading-5 font-semibold'
            )}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={saving}
              className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors"
            >
              {t('inventory.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !dateValue}
              className="flex-1 px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
